import { useSelector } from "react-redux";
import { Helmet } from "react-helmet-async";
import DashboardLayout from "../../hocs/layout/DashboardLayout";
import profileImg from "../../assets/img/profile-img.png";
import { 
  IconDiscountCheck, 
  IconPencil, 
  IconMail, 
  IconPhone, 
  IconCalendar,
  IconUser,
  IconBuildingStore,
  IconShield
} from "@tabler/icons-react";
import Loading from "../../components/Loading";
import bannerImg from "../../assets/img/banner.png";
import EditProfileModal from "../../components/profile/EditProfileModal";

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Meta } from "../../components/navigation/Helmet";
import axios from "axios";

function Profile() {
	const user = useSelector((state) => state.auth.user);
	const [profileData, setProfileData] = useState(null);
	const [showEditModal, setShowEditModal] = useState(false);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		fetchProfileData();
	}, []);

	const fetchProfileData = async () => {
		try {
			const token = localStorage.getItem("access");
			const config = {
				headers: {
					Authorization: `JWT ${token}`,
					Accept: "application/json",
				},
			};

			const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/profile/`, config);
			setProfileData(res.data);
		} catch (error) {
			console.error("Error fetching profile data:", error);
		} finally {
			setLoading(false);
		}
	};

	const handleUpdateProfile = (updatedData) => {
		setProfileData(updatedData);
	};

	const formatDate = (dateString) => {
		if (!dateString) return "No disponible";
		return new Date(dateString).toLocaleDateString('es-ES', {
			year: 'numeric',
			month: 'long',
			day: 'numeric'
		});
	};

	const getUserTypeLabel = (userType) => {
		const types = {
			'NORMAL': 'Usuario Normal',
			'BUSINESS': 'Usuario Empresarial',
			'ADMIN': 'Administrador'
		};
		return types[userType] || userType;
	};

	return (
		<DashboardLayout>
			<Meta title="Perfil" />
			{loading ? (
				<div className="w-full h-full flex justify-center items-center">
					<Loading />
				</div>
			) : (
				<div className="container mx-auto max-w-4xl relative">
					<div className="banner-profile">
						<img src={bannerImg} className="rounded-xl" alt="Banner profile" />
					</div>
					{profileData ? (
						<div className="flex flex-col items-center relative">
							<div className="flex flex-col md:flex-row md:justify-between lg:absolute -top-6 lg:-top-10 px-4 py-3 bg-white md:bg-transparent shadow-lg w-full rounded-b-lg md:shadow-none">
								<div className="profile-data flex flex-col md:flex-row items-center gap-4">
									<div className="relative drop-shadow-md">
										<div className="p-1 rounded-full overflow-hidden w-20 h-20 md:w-32 md:h-32 flex justify-center items-center bg-white">
											<div
												className={
													"p-1 rounded-full overflow-hidden w-16 h-16 md:w-28 md:h-28 flex justify-center items-center bg-yellow-500"
												}
											>
												{profileData.avatar ? (
													<img
														src={profileData.avatar}
														className="h-full w-full object-cover rounded-full"
														alt="Profile avatar"
													/>
												) : (
													<img
														src={profileImg}
														className="h-12 w-12 md:h-24 md:w-24"
														alt="Default profile"
													/>
												)}
											</div>
										</div>
										<div>
											{profileData && profileData.verified ? (
												<div className="bg-green-500 rounded-full p-0.5 absolute bottom-2.5 right-2.5">
													<IconDiscountCheck className="text-white" />
												</div>
											) : (
												<div className="bg-gray-400 rounded-full p-0.5 absolute bottom-2.5 right-2.5">
													<IconDiscountCheck className="text-white" />
												</div>
											)}
										</div>
									</div>
									<div className="flex flex-col items-center md:items-start">
										<h2 className="font-bold text-lg uppercase text-[#00496B]">
											{profileData.first_name && profileData.last_name 
												? `${profileData.first_name} ${profileData.last_name}`
												: profileData.username || "Usuario"
											}
										</h2>
										<h3 className="text-sm text-[#00496B]">
											@{profileData.username}
										</h3>
										<div className="flex items-center space-x-2 mt-1">
											<IconShield className="h-4 w-4 text-[#00496B]" />
											<span className="text-sm text-[#00496B]">
												{getUserTypeLabel(profileData.user_type)}
											</span>
										</div>
									</div>
								</div>
								<div className="flex items-center justify-center md:justify-end mt-4 md:mt-0">
									<button
										onClick={() => setShowEditModal(true)}
										className="p-3 text-white bg-blue-500 hover:bg-blue-600 rounded-full transition-colors duration-200"
									>
										<IconPencil className="h-5 w-5" />
									</button>
								</div>
							</div>
							
							{/* Información detallada */}
							<div className="mt-5 lg:mt-32 w-full">
								<div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
									<h3 className="text-lg font-semibold text-gray-900 mb-4">Información Personal</h3>
									<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
										<div className="flex items-center space-x-3">
											<div className="p-2 bg-blue-100 rounded-full">
												<IconUser className="h-5 w-5 text-blue-600" />
											</div>
											<div>
												<p className="text-sm text-gray-600">Nombre Completo</p>
												<p className="font-medium text-gray-900">
													{profileData.first_name && profileData.last_name 
														? `${profileData.first_name} ${profileData.last_name}`
														: "No especificado"
													}
												</p>
											</div>
										</div>

										<div className="flex items-center space-x-3">
											<div className="p-2 bg-green-100 rounded-full">
												<IconMail className="h-5 w-5 text-green-600" />
											</div>
											<div>
												<p className="text-sm text-gray-600">Email</p>
												<p className="font-medium text-gray-900">
													{profileData.email || "No especificado"}
												</p>
												{profileData.verified && (
													<span className="inline-flex items-center px-2 py-1 text-xs font-medium text-green-800 bg-green-100 rounded-full mt-1">
														Verificado
													</span>
												)}
											</div>
										</div>

										<div className="flex items-center space-x-3">
											<div className="p-2 bg-purple-100 rounded-full">
												<IconPhone className="h-5 w-5 text-purple-600" />
											</div>
											<div>
												<p className="text-sm text-gray-600">Teléfono</p>
												<p className="font-medium text-gray-900">
													{profileData.phone || "No especificado"}
												</p>
											</div>
										</div>

										<div className="flex items-center space-x-3">
											<div className="p-2 bg-orange-100 rounded-full">
												<IconCalendar className="h-5 w-5 text-orange-600" />
											</div>
											<div>
												<p className="text-sm text-gray-600">Fecha de Registro</p>
												<p className="font-medium text-gray-900">
													{formatDate(profileData.date_joined)}
												</p>
											</div>
										</div>
									</div>

									{profileData.bio && (
										<div className="mt-6">
											<h4 className="text-sm font-medium text-gray-600 mb-2">Biografía</h4>
											<p className="text-gray-900 bg-gray-50 p-3 rounded-lg">
												{profileData.bio}
											</p>
										</div>
									)}
								</div>

								{/* Información de cuenta */}
								<div className="bg-white rounded-lg shadow-sm border p-6">
									<h3 className="text-lg font-semibold text-gray-900 mb-4">Estado de Cuenta</h3>
									<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
										<div className="text-center p-4 bg-gray-50 rounded-lg">
											<div className={`inline-flex p-3 rounded-full mb-2 ${
												profileData.is_active ? 'bg-green-100' : 'bg-red-100'
											}`}>
												<IconUser className={`h-6 w-6 ${
													profileData.is_active ? 'text-green-600' : 'text-red-600'
												}`} />
											</div>
											<p className="text-sm text-gray-600">Estado de Cuenta</p>
											<p className={`font-semibold ${
												profileData.is_active ? 'text-green-800' : 'text-red-800'
											}`}>
												{profileData.is_active ? 'Activa' : 'Inactiva'}
											</p>
										</div>

										<div className="text-center p-4 bg-gray-50 rounded-lg">
											<div className={`inline-flex p-3 rounded-full mb-2 ${
												profileData.verified ? 'bg-blue-100' : 'bg-yellow-100'
											}`}>
												<IconDiscountCheck className={`h-6 w-6 ${
													profileData.verified ? 'text-blue-600' : 'text-yellow-600'
												}`} />
											</div>
											<p className="text-sm text-gray-600">Verificación</p>
											<p className={`font-semibold ${
												profileData.verified ? 'text-blue-800' : 'text-yellow-800'
											}`}>
												{profileData.verified ? 'Verificado' : 'Pendiente'}
											</p>
										</div>

										<div className="text-center p-4 bg-gray-50 rounded-lg">
											<div className={`inline-flex p-3 rounded-full mb-2 ${
												profileData.is_staff ? 'bg-purple-100' : 'bg-gray-100'
											}`}>
												<IconBuildingStore className={`h-6 w-6 ${
													profileData.is_staff ? 'text-purple-600' : 'text-gray-600'
												}`} />
											</div>
											<p className="text-sm text-gray-600">Permisos</p>
											<p className={`font-semibold ${
												profileData.is_staff ? 'text-purple-800' : 'text-gray-800'
											}`}>
												{profileData.is_staff ? 'Staff' : 'Usuario'}
											</p>
										</div>
									</div>
								</div>
							</div>
						</div>
					) : (
						<div className="w-full h-full flex justify-center items-center">
							<div className="text-center">
								<p className="text-gray-600">No se pudo cargar la información del perfil</p>
								<button 
									onClick={fetchProfileData}
									className="mt-2 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
								>
									Reintentar
								</button>
							</div>
						</div>
					)}

					{/* Modal de edición */}
					<EditProfileModal
						isOpen={showEditModal}
						onClose={() => setShowEditModal(false)}
						user={profileData}
						onUpdate={handleUpdateProfile}
					/>
				</div>
			)}
		</DashboardLayout>
	);
}
export default Profile;
